import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserManager, getCachedSubForSid } from './oidcManager';
import { initEsignSSO } from './initEsignSSO';

/**
 * React hook returning a fresh esign-audienced OIDC access token for the
 * given UV SessionID. Bootstraps via `/oidc/trusted-token` on first use,
 * then re-uses the cached AT until it expires (silent renew handled by the
 * UserManager). Returns `null` while loading or if the UV session is
 * invalid.
 *
 * Mirror of `Components/TrustShield/useTrustShieldAuth.ts`.
 */
export function useEsignAuth(sid: string, enabled: boolean) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!sid) return;
        let cancelled = false;
        let cleanup: (() => void) | null = null;
        (async () => {
            const sub = await initEsignSSO(sid);
            if (cancelled || !sub) return;
            const mgr = getUserManager(sub);
            const onUserLoaded = () => {
                queryClient.invalidateQueries({ queryKey: ['esignJwt'] });
            };
            mgr.events.addUserLoaded(onUserLoaded);
            cleanup = () => mgr.events.removeUserLoaded(onUserLoaded);
        })();
        return () => {
            cancelled = true;
            cleanup?.();
        };
    }, [queryClient, sid]);

    const { data: jwt, isLoading, error } = useQuery<string>({
        queryKey: ['esignJwt', sid],
        queryFn: async () => {
            const cachedSub = getCachedSubForSid(sid);
            if (cachedSub) {
                const user = await getUserManager(cachedSub).getUser();
                if (user && !user.expired) return user.access_token;
            }

            const refreshedSub = await initEsignSSO(sid);
            if (!refreshedSub) throw new Error('No valid OIDC access token.');
            const user = await getUserManager(refreshedSub).getUser();
            if (user && !user.expired) return user.access_token;

            throw new Error('No valid OIDC access token.');
        },
        enabled: enabled && Boolean(sid),
        refetchOnWindowFocus: true,
    });

    return { jwt: jwt ?? null, isLoading, error };
}
